import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { mainNavigation } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const { phone } = siteConfig.contact

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    const isActive = (href: string): boolean => !href.includes('#') && router.pathname === href

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300',
                scrolled || open
                    ? 'border-border bg-background/90 shadow-[0_1px_12px_-6px_rgb(0_0_0/0.12)] backdrop-blur-md'
                    : 'border-transparent bg-background'
            )}
        >
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8 lg:h-20'>
                <Link
                    href='/'
                    aria-label={`${siteConfig.name} home`}
                    className='rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background'
                >
                    <Logo />
                </Link>

                <nav aria-label="Main" className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {mainNavigation.map(item => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={isActive(item.href) ? 'page' : undefined}
                                    className={cn(
                                        'rounded-md px-3 py-2 text-sm font-medium transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        isActive(item.href) ? 'text-foreground' : 'text-foreground/70'
                                    )}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2 sm:gap-3'>
                    {phone && (
                        <a
                            href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                            className='hidden items-center gap-2 rounded-md px-2 py-2 text-sm font-semibold text-foreground hover:text-brand-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring xl:inline-flex'
                        >
                            <Phone aria-hidden='true' className='size-4' />
                            {phone}
                        </a>
                    )}

                    <ButtonLink href='/contact' size='sm' className='hidden sm:inline-flex'>
                        Get a free quote
                    </ButtonLink>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-navigation'
                        onClick={() => setOpen(value => !value)}
                        className='inline-flex size-10 items-center justify-center rounded-md text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden'
                    >
                        {open ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}

                        <span className='sr-only'>
                            {open ? 'Close menu' : 'Open menu'}
                        </span>
                    </button>
                </div>
            </div>

            <div id='mobile-navigation' className={cn('border-t border-border bg-background lg:hidden', open ? 'block' : 'hidden')}>
                <nav aria-label="Mobile" className='mx-auto max-w-7xl px-5 pb-6 pt-3 sm:px-8'>
                    <ul className='flex flex-col'>
                        {mainNavigation.map(item => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={isActive(item.href) ? 'page' : undefined}
                                    className='block rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-4 flex flex-col gap-3 sm:hidden'>
                        <ButtonLink href='/contact' size='lg' className='w-full'>
                            Get a free quote
                        </ButtonLink>

                        {phone && (
                            <ButtonLink href={`tel:${phone.replace(/[^\d+]/g, '')}`} variant='outline' size='lg' className='w-full'>
                                <Phone aria-hidden='true' />
                                {phone}
                            </ButtonLink>
                        )}
                    </div>
                </nav>
            </div>
        </header>
    )
}
